import { Decimal } from '@finance/domain-core';
import { registerPayloadBuilder } from './payload-builder-registry';
import { registerPayloadSchema } from './payload-schema-registry';

const DECIMAL = /^(?:\d+(?:\.\d+)?|\.\d+)$/;

function journalPayload(payload: Record<string, unknown>): Record<string, unknown> {
  if (!Array.isArray(payload.lines) || payload.lines.length < 2) throw new Error('Journal requires at least two lines');
  let debit = Decimal.zero();
  let credit = Decimal.zero();
  const lines = payload.lines.map((value, index) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`Journal line ${index + 1} is invalid`);
    const line = value as Record<string, unknown>;
    const account = String(line.ledgerAccountId ?? '').trim();
    const debitValue = String(line.debit ?? '').trim() || '0';
    const creditValue = String(line.credit ?? '').trim() || '0';
    if (!account || !DECIMAL.test(debitValue) || !DECIMAL.test(creditValue)) throw new Error(`Journal line ${index + 1} is invalid`);
    const lineDebit = Decimal.fromString(debitValue);
    const lineCredit = Decimal.fromString(creditValue);
    if (lineDebit.isPositive() === lineCredit.isPositive()) throw new Error(`Journal line ${index + 1} needs exactly one side`);
    debit = debit.plus(lineDebit);
    credit = credit.plus(lineCredit);
    return { ledgerAccountId: account, debit: debitValue, credit: creditValue, ...(String(line.narration ?? '').trim() ? { narration: String(line.narration).trim() } : {}) };
  });
  if (!debit.isPositive() || !debit.eq(credit)) throw new Error('Journal debit and credit totals must balance');
  return { entryDate: String(payload.entryDate ?? '').trim(), fiscalYearId: String(payload.fiscalYearId ?? '').trim(), ...(String(payload.reference ?? '').trim() ? { reference: String(payload.reference).trim() } : {}), ...(String(payload.narration ?? '').trim() ? { narration: String(payload.narration).trim() } : {}), lines };
}

registerPayloadSchema({ id: '#/schemas/command.journals.create', allowedKeys: ['entryDate', 'fiscalYearId', 'reference', 'narration', 'lines'], requiredKeys: ['entryDate', 'fiscalYearId', 'lines'], parser: journalPayload });
registerPayloadBuilder({ id: 'builder.journal.create' as never, buildWithProvenance: values => ({ payload: { ...values }, provenance: Object.fromEntries(Object.keys(values).map(key => [key, [key]])) }) });
